import { gtag, install } from 'ga-gtag';
import { CONFIG } from '../utils/config';
import { store } from '@headless-shell/store';
import { getCookie, getPageType } from '../utils/helper';

const isAnalyticsEnabled = () => CONFIG.ENABLE_ANALYTICS;
const canSendGA4 = () => isAnalyticsEnabled() && Boolean(CONFIG.REACT_APP_GA4_ID);
const canLoadGTM = () => isAnalyticsEnabled() && Boolean(CONFIG.REACT_APP_GTM_ID);

const loadGTM = async () => {
    if (!canLoadGTM()) return;

    (function (w, d, s, l, i) {
      w[l] = w[l] || [];
      w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      const f = d.getElementsByTagName(s)[0];
      const j = d.createElement(s);
      const dl = l !== 'dataLayer' ? '&l=' + l : '';
      j.async = true;
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
      f.parentNode.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', CONFIG.REACT_APP_GTM_ID);
};

function ga4Init () {
  if (!canSendGA4()) return;

  install(CONFIG.REACT_APP_GA4_ID);
  // Pin the cookie to this host so _ga_* is not written to the shared .adp.com
  // apex (avoids leaking to other subdomains and duplicate apex cookies).
  gtag('config', CONFIG.REACT_APP_GA4_ID, {
    cookie_domain: window.location.hostname
  });
}

const getUserInfo = () => {
  try {
    return store.getState()?.userContext?.userInfo || null;
  } catch (e) {
    console.warn('getUserInfo failed', e);
    return null;
  }
};

function getCommonDataForGA4() {
  const userInfo = getUserInfo();

  return {
    gclientId: getCookie("_ga") || "",
    loginStatus: localStorage.id_token?.length ? "logged_in" : "logged_out",
    page_type: getPageType(),
    pageType: getPageType(),
    adpWebId: userInfo?.adpWebID
  };
}

function getUserPropertiesForGA4() {
  const userInfo = getUserInfo();
  const userAppStoreProfiles = userInfo?.userAppStoreProfiles;
  const appDirectInfo = userInfo?.appDirectInfo || {};

  return {
    adpUuid: userInfo?.adpWebID,
    userId: appDirectInfo?.user?.id,
    clientIndustry: appDirectInfo?.company?.industry || "",
    productChannel: userAppStoreProfiles?.productChannelCode?.join(),
    businessType: userAppStoreProfiles?.businessTypeCodes?.join(),
    gaUserId: getCookie("_ga") || "",
  };
}

/**
 * Sets window.customDimensions object with user analytics properties.
 * This makes customDimensions available in the browser console for debugging
 * and for external analytics tools.
 * For logged out users, values are set to "empty" to match existing AppStore behavior.
 */
function setCustomDimensions() {
  const userInfo = getUserInfo();
  const userAppStoreProfiles = userInfo?.userAppStoreProfiles;

  window.customDimensions = {
    adpUuid: userInfo?.adpWebID || 'empty',
    productChannel: userAppStoreProfiles?.productChannelCode?.join() || 'empty',
    businessType: userAppStoreProfiles?.businessTypeCodes?.join() || 'empty',
    individualPurchaseIndicator: userAppStoreProfiles?.individualPurchaseIndicator ?? false,
  };
}

const sendDataToGA4 = (eventName, dataObj = {}) => {
  if (!canSendGA4()) return;

  const {
    adpUuid,
    userId,
    clientIndustry,
    productChannel,
    businessType,
    gaUserId,
    ...eventData
  } = dataObj;

  // Ensure both camelCase and snake_case versions exist for GA fields
  ['eventAction', 'eventCategory', 'eventLabel'].forEach((key) => {
    const val = eventData[key];
    if (val) {
      const snake = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      eventData[snake] = val;
      eventData[key] = val;
    }
  });

  try {
    gtag('set', 'user_properties', getUserPropertiesForGA4());
    gtag('event', eventName.toLowerCase(), {
      ...getCommonDataForGA4(),
      ...eventData
    });
  } catch (e) {
    console.warn('sendDataToGA4 failed', e);
  }
};

export {
  loadGTM,
  ga4Init, 
  sendDataToGA4,
  setCustomDimensions
};
